export const LIVE_HOST = 'live.cynoops.com';
export const LIVE_SNAPSHOT_ENDPOINT = '/api/live-session';

const TOKEN_ID_PATTERN = /^[A-Za-z0-9_-]{12,96}$/;
const TOKEN_SECRET_PATTERN = /^[A-Za-z0-9_-]{32,160}$/;

export class LiveSessionError extends Error {
  constructor(code, message, status = 0) {
    super(message || code || 'live-session-error');
    this.name = 'LiveSessionError';
    this.code = code || 'generic';
    this.status = Number(status) || 0;
  }
}

export const isShareCredentials = (value) => Boolean(
  value
    && TOKEN_ID_PATTERN.test(String(value.tokenId || ''))
    && TOKEN_SECRET_PATTERN.test(String(value.token || ''))
);

const decodeHashPart = (value) => {
  try {
    return decodeURIComponent(String(value || ''));
  } catch {
    return String(value || '');
  }
};

const credentialsFromUrl = (url) => {
  const hash = String(url.hash || '').replace(/^#/, '');
  const sessionMatch = /^\/?(?:session|s)\/([^/]+)\/([^/?#]+)/i.exec(hash);
  if (sessionMatch) {
    return {
      tokenId: decodeHashPart(sessionMatch[1]),
      token: decodeHashPart(sessionMatch[2])
    };
  }

  const hashParams = new URLSearchParams(hash.replace(/^\?/, ''));
  const queryTokenId = hashParams.get('tokenId') || hashParams.get('shareId');
  const queryToken = hashParams.get('token') || hashParams.get('shareToken');
  if (queryTokenId && queryToken) return { tokenId: queryTokenId, token: queryToken };

  // Legacy query-string support is accepted for imports, but new links always use a fragment.
  const tokenId = url.searchParams.get('tokenId') || url.searchParams.get('shareId');
  const token = url.searchParams.get('token') || url.searchParams.get('shareToken');
  return tokenId && token ? { tokenId, token } : null;
};

const parseObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  if (typeof value.url === 'string') return parseShareInput(value.url);
  if (typeof value.liveUrl === 'string') return parseShareInput(value.liveUrl);
  if (typeof value.shareUrl === 'string') return parseShareInput(value.shareUrl);
  return {
    tokenId: String(value.tokenId ?? value.shareId ?? value.id ?? ''),
    token: String(value.token ?? value.shareToken ?? value.secret ?? '')
  };
};

export const parseShareInput = (input) => {
  const raw = typeof input === 'string' ? input.trim() : '';
  if (!raw) throw new LiveSessionError('invalid', 'Empty share payload.');

  let candidate = null;
  if (raw.startsWith('{')) {
    try {
      candidate = parseObject(JSON.parse(raw));
    } catch {
      candidate = null;
    }
  }

  if (!candidate) {
    try {
      const url = new URL(raw, `https://${LIVE_HOST}/`);
      candidate = credentialsFromUrl(url);
      const isLiveOrigin = url.hostname === LIVE_HOST
        || url.hostname === 'com-cynoops-live.web.app'
        || url.hostname === 'localhost'
        || url.hostname === '127.0.0.1';
      if (!isLiveOrigin && /^https?:/i.test(url.protocol)) candidate = null;
    } catch {
      candidate = null;
    }
  }

  if (!isShareCredentials(candidate)) {
    throw new LiveSessionError('invalid', 'Invalid CYNOOPS LIVE share payload.');
  }
  return { tokenId: candidate.tokenId, token: candidate.token };
};

export const buildShareUrl = ({ tokenId, token }, origin = `https://${LIVE_HOST}`) => {
  if (!isShareCredentials({ tokenId, token })) {
    throw new LiveSessionError('invalid', 'Invalid share credentials.');
  }
  return `${String(origin || '').replace(/\/$/, '')}/#/session/${encodeURIComponent(tokenId)}/${encodeURIComponent(token)}`;
};

export const readShareFromLocation = (locationLike = globalThis.location) => {
  if (!locationLike) return null;
  try {
    const raw = locationLike.href || `${locationLike.origin || ''}/${locationLike.hash || ''}`;
    return parseShareInput(raw);
  } catch {
    return null;
  }
};

export const fetchLiveSnapshot = async (
  credentials,
  { endpoint = LIVE_SNAPSHOT_ENDPOINT, signal, fetchImpl = globalThis.fetch } = {}
) => {
  if (!isShareCredentials(credentials)) throw new LiveSessionError('invalid', 'Invalid share credentials.');
  if (typeof fetchImpl !== 'function') throw new LiveSessionError('network', 'Fetch is unavailable.');

  let response;
  try {
    response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal,
      body: JSON.stringify({ data: credentials })
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new LiveSessionError('network', error?.message || 'Network request failed.');
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch {}
  const callableError = payload?.error && typeof payload.error === 'object' ? payload.error : null;
  const result = payload?.result ?? payload?.data ?? payload;
  if (!response.ok || callableError || result?.ok === false) {
    const status = Number(callableError?.details?.httpStatus) || response.status;
    const code = String(
      callableError?.details?.liveCode
      || result?.error
      || result?.code
      || (status === 410 ? 'expired' : 'generic')
    );
    throw new LiveSessionError(
      code,
      callableError?.message || result?.message || `Snapshot request failed (${status}).`,
      status
    );
  }
  if (!result?.session || !result?.map) {
    throw new LiveSessionError('invalid-response', 'Snapshot response is incomplete.', response.status);
  }
  return result;
};
